import { Plus, X } from "lucide-react";
import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ChatRoomListSection } from "@/features/chat/components/ChatRoomListSection";
import { ChatRoomParticipantsBadges } from "@/features/chat/components/ChatRoomParticipantsBadges";
import { RecipientSelectorDialog } from "@/features/chat/components/RecipientSelectorDialog";
import { useParticipantsSearchState } from "@/features/chat/hooks/useParticipantsSearchState";
import { mapChatRoomsToListItems } from "@/features/chat/utils/chatRoomList";
import { useSearchChatEntries } from "@/hooks/useSearchChatEntries";
import { useSearchChatRooms } from "@/hooks/useSearchChatRooms";
import type { ChatRoomResponse, User, UserShort } from "@/lib/humhub-api/types";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

const LoadingState: React.FC<{ label: string }> = ({ label }) => (
  <div className="py-8 text-center">
    <div className="mx-auto size-8 animate-spin rounded-full border-b-2 border-rw-main" />
    <p className="mt-2 text-sm text-gray-500">{label}</p>
  </div>
);

const buildHitReasonBadges = (
  t: (key: string) => string,
  hitReasons?: string[],
): ReactNode => {
  if (!hitReasons || hitReasons.length === 0) {
    return null;
  }

  const normalized = Array.from(new Set(hitReasons));
  const labels = normalized
    .map((reason) => {
      if (reason === "title") return t("chat.searchHitTitle");
      if (reason === "participant") return t("chat.searchHitParticipant");
      if (reason === "content") return t("chat.searchHitContent");
      return null;
    })
    .filter((label): label is string => Boolean(label));

  if (labels.length === 0) {
    return null;
  }

  return labels.map((label) => (
    <Badge
      key={label}
      variant="secondary"
      className="pointer-events-none select-none border border-rw-gray-200 bg-white px-1.5 py-0 text-[9px] leading-4 text-rw-mid-gray"
    >
      {label}
    </Badge>
  ));
};

const buildEntryMatchedRoomIds = (
  results?: Array<{ message_id?: number | null }>,
) =>
  new Set(
    (results ?? [])
      .map((entry) => entry.message_id)
      .filter((id): id is number => typeof id === "number"),
  );

const buildSearchHitReasons = (
  room: ChatRoomResponse,
  matchedRoomIds: Set<number>,
): string[] => {
  const reasons = [...(room.hit_reasons ?? [])];
  if (matchedRoomIds.has(room.id)) {
    reasons.push("content");
  }
  return reasons;
};

interface ChatSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChatRoomSelect: (roomId: number | undefined) => void;
  onCreateNewChat?: (selectedUser?: User) => void;
  initialUser?: User;
  initialParticipants?: UserShort[];
  initialSearchMode?: "keyword" | "participants";
}

export const ChatSearchModal = ({
  isOpen,
  onClose,
  onChatRoomSelect,
  onCreateNewChat,
  initialUser,
  initialParticipants,
  initialSearchMode,
}: ChatSearchModalProps) => {
  const { t } = useTranslation();
  const searchModeIdBase = useId();
  const keywordSearchModeId = `${searchModeIdBase}-keyword`;
  const participantsSearchModeId = `${searchModeIdBase}-participants`;
  const [keyword, setKeyword] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(
    initialUser,
  );
  const [searchMode, setSearchMode] = useState<"keyword" | "participants">(
    "keyword",
  );
  const [selectedParticipants, setSelectedParticipants] = useState<UserShort[]>(
    [],
  );
  const [isParticipantDialogOpen, setIsParticipantDialogOpen] = useState(false);
  const isSearching = useRef(false);
  const userInfo = useGlobalUserInfo();

  // チャットルーム検索用のフック（キーワード検索：DM）
  const dmSearch = useSearchChatRooms(undefined, "user_dm");

  // チャットルーム検索用のフック（キーワード検索：トピック）
  const topicSearch = useSearchChatRooms(undefined, "user_group");
  const dmEntrySearch = useSearchChatEntries(undefined, "user_dm");
  const topicEntrySearch = useSearchChatEntries(undefined, "user_group");

  const dmEntryMatchedRoomIds = useMemo(
    () => buildEntryMatchedRoomIds(dmEntrySearch.response?.results),
    [dmEntrySearch.response?.results],
  );

  const topicEntryMatchedRoomIds = useMemo(
    () => buildEntryMatchedRoomIds(topicEntrySearch.response?.results),
    [topicEntrySearch.response?.results],
  );

  const {
    hasParticipants,
    existingDmChats,
    existingTopicChats,
    isCheckingExisting,
    error: participantsError,
  } = useParticipantsSearchState({
    isOpen,
    searchMode,
    selectedParticipants,
    currentUserId: userInfo?.id,
  });

  // initialUserが変更されたらselectedUserを更新
  useEffect(() => {
    setSelectedUser(initialUser);
    if (initialUser) {
      const userName =
        `${initialUser.profile.firstname} ${initialUser.profile.lastname}`.trim();
      setKeyword(userName);
    }
  }, [initialUser]);

  // キーワードが変更されたら検索をリセット
  // biome-ignore lint/correctness/useExhaustiveDependencies: 必要な場合のみ再実行
  useEffect(() => {
    if (isOpen) {
      setSearchPerformed(false);
    }
  }, [keyword, isOpen, searchMode, selectedParticipants.length]);

  // モーダルの開閉状態管理
  useEffect(() => {
    if (isOpen) {
      if (!initialUser) {
        setKeyword("");
        setSelectedUser(undefined);
      }
      setSelectedParticipants(initialParticipants ?? []);
      setSearchMode(initialSearchMode ?? "keyword");
      setSearchPerformed(false);
      isSearching.current = false;
    } else {
      // モーダルが閉じた時のリセット
      isSearching.current = false;
      setKeyword("");
      setSelectedUser(undefined);
      setSelectedParticipants([]);
      setSearchMode("keyword");
      setSearchPerformed(false);
    }
  }, [isOpen, initialUser, initialParticipants, initialSearchMode]);

  const keywordSearchConfigs = useMemo(
    () => [
      {
        key: "messages",
        type: "user_dm" as const,
        title: t("chat.sectionMessages"),
        response: dmSearch.response,
        isLoading: dmSearch.isLoading,
        error: dmSearch.error,
        clearSearch: dmSearch.clearSearch,
        search: dmSearch.searchChatRooms,
        mapRooms: (rooms: ChatRoomResponse[]) =>
          mapChatRoomsToListItems(rooms).map((room, index) => ({
            ...room,
            sublabelRightContent: buildHitReasonBadges(
              t,
              buildSearchHitReasons(rooms[index], dmEntryMatchedRoomIds),
            ),
          })),
      },
      {
        key: "topics",
        type: "user_group" as const,
        title: t("chat.sectionTopics"),
        response: topicSearch.response,
        isLoading: topicSearch.isLoading,
        error: topicSearch.error,
        clearSearch: topicSearch.clearSearch,
        search: topicSearch.searchChatRooms,
        mapRooms: (rooms: ChatRoomResponse[]) =>
          rooms.map((room) => ({
            id: room.id,
            title: room.title,
            imageUrl: room.image_url,
            lastEntryContent: undefined,
            sublabel: (
              <ChatRoomParticipantsBadges displayTitle={room.display_title} />
            ),
            sublabelRightContent: buildHitReasonBadges(
              t,
              buildSearchHitReasons(room, topicEntryMatchedRoomIds),
            ),
          })),
      },
    ],
    [
      t,
      dmSearch.response,
      dmSearch.isLoading,
      dmSearch.error,
      dmSearch.clearSearch,
      dmSearch.searchChatRooms,
      dmEntryMatchedRoomIds,
      topicSearch.response,
      topicSearch.isLoading,
      topicSearch.error,
      topicSearch.clearSearch,
      topicSearch.searchChatRooms,
      topicEntryMatchedRoomIds,
    ],
  );

  // 検索実行関数
  const performSearch = useCallback(async () => {
    const searchTerm = keyword.trim();
    if (isSearching.current) return;

    if (searchMode === "keyword" && !searchTerm) {
      return;
    }

    if (searchMode === "participants" && !hasParticipants) {
      return;
    }

    if (searchMode === "participants" && isCheckingExisting) {
      return;
    }

    isSearching.current = true;
    setSearchPerformed(true);

    try {
      // チャットルーム検索
      try {
        if (searchMode === "keyword") {
          await Promise.all(
            keywordSearchConfigs.map((config) =>
              config.search(searchTerm, config.type),
            ),
          );
          await Promise.all([
            dmEntrySearch.searchChatEntries(searchTerm, "user_dm"),
            topicEntrySearch.searchChatEntries(searchTerm, "user_group"),
          ]);
        }
      } catch (roomError) {
        console.error("チャットルーム検索エラー:", roomError);
      }
    } catch (error) {
      console.error("検索失敗:", error);
    } finally {
      isSearching.current = false;
    }
  }, [
    keyword,
    searchMode,
    hasParticipants,
    isCheckingExisting,
    keywordSearchConfigs,
    dmEntrySearch,
    topicEntrySearch,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      searchMode !== "participants" ||
      !hasParticipants ||
      isCheckingExisting ||
      searchPerformed
    ) {
      return;
    }

    setSearchPerformed(true);
  }, [
    isOpen,
    searchMode,
    hasParticipants,
    isCheckingExisting,
    searchPerformed,
  ]);

  // 検索ボタンクリックハンドラ
  const handleSearch = () => {
    performSearch();
  };

  // Enterキー押下ハンドラ
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      performSearch();
    }
  };

  // 新規チャット作成ハンドラ
  const handleCreateNewChat = () => {
    if (onCreateNewChat) {
      onCreateNewChat(selectedUser);
    }
    onClose();
  };

  const handleChatRoomClick = (roomId: number | undefined) => {
    onChatRoomSelect(roomId);
    onClose();
  };

  const keywordIsLoading = keywordSearchConfigs.some(
    (config) => config.isLoading,
  );
  const keywordError = keywordSearchConfigs.find(
    (config) => config.error,
  )?.error;
  const keywordResults = keywordSearchConfigs.map(
    (config) => config.response?.results || [],
  );
  const isLoading =
    searchMode === "keyword" ? keywordIsLoading : isCheckingExisting;
  const hasError = searchMode === "keyword" ? keywordError : participantsError;
  const errorMessage =
    searchMode === "keyword" ? keywordError : participantsError;
  const noResults =
    searchPerformed &&
    !isLoading &&
    !hasError &&
    (searchMode === "participants"
      ? existingDmChats.length === 0 && existingTopicChats.length === 0
      : keywordResults.every((results) => results.length === 0));

  // データ準備中の表示
  const isPreparingData =
    searchMode === "participants" ? isCheckingExisting : false;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("chat.searchTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <RadioGroup
              value={searchMode}
              onValueChange={(value) =>
                setSearchMode(value as typeof searchMode)
              }
            >
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor={keywordSearchModeId}
              >
                <RadioGroupItem value="keyword" id={keywordSearchModeId} />
                {t("chat.searchByKeyword")}
              </label>
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor={participantsSearchModeId}
              >
                <RadioGroupItem
                  value="participants"
                  id={participantsSearchModeId}
                />
                {t("chat.searchByParticipants")}
              </label>
            </RadioGroup>

            {searchMode === "keyword" && (
              <div className="flex items-center space-x-2">
                <Input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("chat.searchPlaceholder")}
                  className="flex-1"
                  disabled={isPreparingData}
                />
                <Button
                  onClick={handleSearch}
                  disabled={
                    !keyword.trim() ||
                    isLoading ||
                    isSearching.current ||
                    isPreparingData
                  }
                  variant="secondary"
                >
                  {t("common.search")}
                </Button>
              </div>
            )}

            {searchMode === "participants" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setIsParticipantDialogOpen(true)}
                    disabled={isPreparingData}
                  >
                    <Plus size={14} className="mr-1" />
                    {t("chat.addSearchParticipant")}
                  </Button>
                  <Button
                    onClick={handleSearch}
                    disabled={
                      selectedParticipants.length === 0 ||
                      isLoading ||
                      isSearching.current ||
                      isPreparingData
                    }
                    variant="secondary"
                  >
                    {t("common.search")}
                  </Button>
                </div>

                {selectedParticipants.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedParticipants.map((participant) => (
                      <Badge
                        key={participant.guid}
                        variant="secondary"
                        className="flex items-center gap-1 bg-white px-2 py-1"
                      >
                        <span className="text-xs">
                          {participant.display_name}
                        </span>
                        <X
                          size={12}
                          className="cursor-pointer hover:text-destructive"
                          onClick={() =>
                            setSelectedParticipants((prev) =>
                              prev.filter((user) => user.id !== participant.id),
                            )
                          }
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {isPreparingData && <LoadingState label={t("common.loading")} />}

          {isLoading && !isPreparingData && (
            <LoadingState label={t("common.searching")} />
          )}

          {hasError && (
            <div className="py-4 text-center text-red-500">{errorMessage}</div>
          )}

          {noResults && (
            <div className="py-4 text-center text-gray-500">
              {t("chat.noSearchResults")}
            </div>
          )}

          {searchPerformed && !isLoading && !hasError && !isPreparingData && (
            <div className="max-h-[400px] space-y-4 overflow-y-auto">
              {searchMode === "participants" && existingDmChats.length > 0 && (
                <ChatRoomListSection
                  title={t("chat.sectionMessages")}
                  rooms={mapChatRoomsToListItems(existingDmChats)}
                  onRoomClick={handleChatRoomClick}
                />
              )}

              {searchMode === "participants" &&
                existingTopicChats.length > 0 && (
                  <ChatRoomListSection
                    title={t("chat.sectionTopics")}
                    rooms={mapChatRoomsToListItems(existingTopicChats, {
                      useTitle: true,
                    })}
                    onRoomClick={handleChatRoomClick}
                  />
                )}

              {searchMode === "keyword" &&
                keywordSearchConfigs.map((config) => {
                  const rooms = config.response?.results || [];
                  if (rooms.length === 0) {
                    return null;
                  }
                  return (
                    <ChatRoomListSection
                      key={config.key}
                      title={config.title}
                      rooms={config.mapRooms(rooms)}
                      onRoomClick={handleChatRoomClick}
                    />
                  );
                })}
            </div>
          )}
        </div>

        <DialogFooter>
          {/* 新規チャット作成ボタン */}
          {onCreateNewChat && (
            <Button onClick={handleCreateNewChat} variant="teal">
              <Plus size={16} className="mr-2" />
              {t("chat.createNewChatRoom")}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <RecipientSelectorDialog
        open={isParticipantDialogOpen}
        onClose={() => setIsParticipantDialogOpen(false)}
        onRecipientsSelected={(recipients) => {
          setSelectedParticipants(recipients);
          setIsParticipantDialogOpen(false);
        }}
        initialRecipients={selectedParticipants}
      />
    </Dialog>
  );
};
